import React, { useState, useEffect } from 'react';
import { X, Award, Save, XCircle, AlertCircle, Edit3, Hash, Upload, Image, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

interface Requisito {
  id: string;
  nome: string;
  nivelId?: string;
  createDate?: string;
  updateDate?: string;
  deleteDate?: string;
  ordem?: number;
}

interface Nivel {
  id: string;
  nome: string;
  descricao?: string;
  cor?: string;
  ordenacao?: number;
  ordem?: number; // Mantido para compatibilidade
  ativo: boolean;
  createDate?: string;
  updateDate?: string;
  imagem?: string;
  requisitos?: { [key: string]: Requisito } | Requisito[];
}

interface NivelModalProps {
  nivel: Nivel | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (nivelData: Partial<Nivel>) => Promise<void>;
}

const NivelModal: React.FC<NivelModalProps> = ({ nivel, isOpen, onClose, onSave }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!nivel);
  const [formData, setFormData] = useState<Partial<Nivel>>({});
  const [requisitos, setRequisitos] = useState<Requisito[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagemPreview, setImagemPreview] = useState<string>('');
  const [editingRequisito, setEditingRequisito] = useState<string | null>(null);
  const [novoRequisito, setNovoRequisito] = useState<string>('');

  useEffect(() => {
    if (nivel) {
      const ordenacao = nivel.ordenacao ?? nivel.ordem ?? 0;
      const requisitosData = nivel.requisitos;
      const requisitosArray = requisitosData 
        ? (Array.isArray(requisitosData) 
            ? requisitosData 
            : Object.entries(requisitosData).map(([id, req]: [string, any]) => ({ 
                id: req.id || id, 
                nome: req.nome || '',
                nivelId: req.nivelId || nivel.id,
                createDate: req.createDate,
                updateDate: req.updateDate,
                deleteDate: req.deleteDate
              })))
        : [];
      
      setFormData({
        nome: nivel.nome || '',
        descricao: nivel.descricao || '',
        ordenacao: ordenacao,
        ordem: ordenacao, // Mantido para compatibilidade
        ativo: nivel.ativo !== undefined ? nivel.ativo : true,
        imagem: nivel.imagem || ''
      });
      setRequisitos(requisitosArray);
      setImagemPreview(nivel.imagem || '');
      setIsEditing(false);
    } else {
      setFormData({
        nome: '',
        descricao: '',
        ordenacao: 0,
        ordem: 0,
        ativo: true,
        imagem: ''
      });
      setRequisitos([]);
      setImagemPreview('');
      setIsEditing(true);
    }
    setErrors({});
    setNovoRequisito('');
    setEditingRequisito(null);
  }, [nivel, isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (formData.ordenacao === undefined || formData.ordenacao < 0) {
      newErrors.ordenacao = 'Ordem deve ser um número maior ou igual a zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: Partial<Nivel>) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const uploadToStorage = async (file: File): Promise<string> => {
    if (!userProfile?.empresaCnpj) {
      throw new Error('CNPJ da empresa não encontrado');
    }

    try {
      const timestamp = Date.now();
      const fileName = `nivel-${nivel?.id || 'new'}-${timestamp}.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, `empresas/${userProfile.empresaCnpj}/niveis/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      throw new Error(`Erro ao fazer upload: ${error.message}`);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, imagem: 'Por favor, selecione uma imagem.' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, imagem: 'A imagem deve ter no máximo 5MB.' });
      return;
    }

    try {
      setUploading(true);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagemPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const uploadedUrl = await uploadToStorage(file);
      handleInputChange('imagem', uploadedUrl);
      
      if (errors.imagem) {
        const newErrors = { ...errors };
        delete newErrors.imagem;
        setErrors(newErrors);
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      setErrors({ ...errors, imagem: error.message || 'Erro ao fazer upload da imagem.' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagemPreview('');
    handleInputChange('imagem', '');
  };

  const handleAddRequisito = () => {
    const texto = novoRequisito.trim();
    if (!texto) {
      return;
    }

    const now = new Date().toISOString();
    const newRequisito: Requisito = {
      id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      nome: texto,
      nivelId: nivel?.id,
      createDate: now,
      updateDate: now,
      deleteDate: '',
      ordem: requisitos.length + 1
    };

    setRequisitos([...requisitos, newRequisito]);
    setNovoRequisito('');
  };

  const handleEditRequisito = (id: string, nome: string) => {
    const now = new Date().toISOString();
    setRequisitos(requisitos.map(req => 
      req.id === id ? { ...req, nome, updateDate: now } : req
    ));
    setEditingRequisito(null);
  };

  const handleDeleteRequisito = (id: string) => {
    setRequisitos(requisitos.filter(req => req.id !== id));
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      
      // Converter requisitos para objeto (formato Firebase)
      const requisitosObj: { [key: string]: Requisito } = {};
      requisitos.forEach((req, idx) => {
        requisitosObj[req.id] = {
          id: req.id,
          nome: req.nome,
          nivelId: req.nivelId || nivel?.id,
          createDate: req.createDate || new Date().toISOString(),
          updateDate: new Date().toISOString(),
          deleteDate: req.deleteDate || '',
          ordem: idx + 1
        };
      });

      await onSave({
        ...(nivel || {}),
        ...formData,
        requisitos: requisitos.length > 0 ? requisitosObj : undefined
      });
      setIsEditing(false);
      if (!nivel) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar nível:', error);
      setErrors({ submit: 'Erro ao salvar nível. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (nivel) {
      const ordenacao = nivel.ordenacao ?? nivel.ordem ?? 0;
      const requisitosData = nivel.requisitos;
      const requisitosArray = requisitosData 
        ? (Array.isArray(requisitosData) 
            ? requisitosData 
            : Object.entries(requisitosData).map(([id, req]: [string, any]) => ({ 
                id: req.id || id, 
                nome: req.nome || '',
                nivelId: req.nivelId || nivel.id,
                createDate: req.createDate,
                updateDate: req.updateDate,
                deleteDate: req.deleteDate
              })))
        : [];
      
      setFormData({
        nome: nivel.nome || '',
        descricao: nivel.descricao || '',
        ordenacao: ordenacao,
        ordem: ordenacao,
        ativo: nivel.ativo !== undefined ? nivel.ativo : true,
        imagem: nivel.imagem || ''
      });
      setRequisitos(requisitosArray);
      setImagemPreview(nivel.imagem || '');
      setIsEditing(false);
    } else {
      onClose();
    }
    setErrors({});
    setNovoRequisito('');
    setEditingRequisito(null);
  };

  if (!isOpen) return null;

  const isCreating = !nivel;
  const displayName = nivel?.nome || 'Novo Nível';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Award className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {isCreating ? 'Novo Nível' : (isEditing ? 'Editar Nível' : 'Detalhes do Nível')}
                </h2>
                {nivel && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {nivel.id}</p>}
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              {!isEditing && !isCreating ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                    disabled={saving}
                  >
                    <XCircle className="h-4 w-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2"
                    disabled={saving || uploading}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Award className="h-5 w-5" />
                Informações Básicas
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome do Nível <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Ex: Iniciante, Intermediário, Avançado"
                    />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{nivel?.nome || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ordem <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="number"
                      min="0"
                      value={formData.ordenacao ?? formData.ordem ?? 0}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        handleInputChange('ordenacao', value);
                        handleInputChange('ordem', value); // Mantido para compatibilidade
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.ordenacao ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="0"
                    />
                    {errors.ordenacao && <p className="text-red-500 text-xs mt-1">{errors.ordenacao}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{nivel?.ordenacao ?? nivel?.ordem ?? 'Não definida'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição
                </label>
                {isEditing || isCreating ? (
                  <textarea
                    value={formData.descricao || ''}
                    onChange={(e) => handleInputChange('descricao', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Descreva o nível..."
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{nivel?.descricao || 'Sem descrição'}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Image className="h-5 w-5" />
                Aparência e Status
              </h3>
              
              {/* Upload de Imagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Imagem do Nível
                </label>
                {isEditing || isCreating ? (
                  <div className="space-y-3">
                    {imagemPreview ? (
                      <div className="relative w-full h-48 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center">
                        <img
                          src={imagemPreview}
                          alt="Preview"
                          className="max-w-full max-h-full object-contain rounded-lg"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-50 dark:bg-gray-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <Image className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma imagem</p>
                        </div>
                      </div>
                    )}
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors cursor-pointer">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Fazendo upload...' : 'Selecionar Imagem'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    {errors.imagem && <p className="text-red-500 text-xs mt-1">{errors.imagem}</p>}
                  </div>
                ) : (
                  <div>
                    {nivel?.imagem ? (
                      <div className="w-full h-48 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center">
                        <img
                          src={nivel.imagem}
                          alt={nivel.nome}
                          className="max-w-full max-h-full object-contain rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Sem imagem</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.ativo ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('ativo', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-xs"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                ) : (
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    nivel?.ativo 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {nivel?.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Requisitos */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2 mb-4">
                <Hash className="h-5 w-5" />
                Requisitos
              </h3>
              {(isEditing || isCreating) && (
                <div className="flex items-center gap-2 w-full">
                  <input
                    type="text"
                    value={novoRequisito}
                    onChange={(e) => setNovoRequisito(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddRequisito();
                      }
                    }}
                    placeholder="Adicionar requisito..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddRequisito();
                    }}
                    disabled={!novoRequisito.trim()}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar
                  </button>
                </div>
              )}
            </div>

            {requisitos.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum requisito cadastrado</p>
            ) : (
              <div className="space-y-2">
                {requisitos.map((req, idx) => (
                  <div key={req.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-semibold">
                      {idx + 1}
                    </span>
                    {isEditing || isCreating ? (
                      editingRequisito === req.id ? (
                        <div className="flex-1 flex items-center gap-2">
                          <input
                            type="text"
                            defaultValue={req.nome}
                            onBlur={(e) => handleEditRequisito(req.id, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                            autoFocus
                          />
                          <button
                            onClick={() => setEditingRequisito(null)}
                            className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">{req.nome}</p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingRequisito(req.id)}
                              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 hover:bg-gray-100 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRequisito(req.id)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </>
                      )
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">{req.nome}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NivelModal;
